import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", role: "athlete", sport: "", notes: "" };

export default function WaitlistForm({ source = "waitlist", onSuccess }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, { ...form, source });
      setDone(true);
      setForm(EMPTY);
      toast.success("You're on the list! We'll be in touch.");
      onSuccess?.();
    } catch {
      toast.error("We couldn't add you right now. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 focus:border-white outline-none py-3 text-white placeholder-white/30 transition-colors duration-300";
  const labelCls = "font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40";

  if (done) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-12" data-testid="waitlist-success">
        <CheckCircle2 size={48} className="text-white mb-5" />
        <h3 className="font-display font-black uppercase tracking-tight text-2xl mb-2">You're On The List.</h3>
        <p className="text-white/60 max-w-sm">Thanks for your interest — we'll reach out as soon as it's available.</p>
        <button
          data-testid="waitlist-reset"
          onClick={() => setDone(false)}
          className="mt-6 border border-white/20 text-white uppercase tracking-[0.2em] font-bold text-xs px-6 py-3 hover:bg-white hover:text-black transition-colors"
        >
          Add Another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-6" data-testid="waitlist-form">
      <div className="sm:col-span-1">
        <label className={labelCls}>Name *</label>
        <input data-testid="waitlist-name" value={form.name} onChange={update("name")} className={inputCls} placeholder="Full name" />
      </div>
      <div className="sm:col-span-1">
        <label className={labelCls}>Email *</label>
        <input data-testid="waitlist-email" type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="you@email.com" />
      </div>
      <div className="sm:col-span-1">
        <label className={labelCls}>I am a…</label>
        <select data-testid="waitlist-role" value={form.role} onChange={update("role")} className={`${inputCls} appearance-none`}>
          <option value="athlete" className="bg-[#0b0b0b]">Athlete</option>
          <option value="parent" className="bg-[#0b0b0b]">Parent / Guardian</option>
        </select>
      </div>
      <div className="sm:col-span-1">
        <label className={labelCls}>Sport</label>
        <input data-testid="waitlist-sport" value={form.sport} onChange={update("sport")} className={inputCls} placeholder="e.g. Football" />
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls}>Notes</label>
        <textarea data-testid="waitlist-notes" value={form.notes} onChange={update("notes")} rows={3} className={`${inputCls} resize-none`} placeholder="Anything you'd like us to know?" />
      </div>
      <div className="sm:col-span-2">
        <button
          data-testid="waitlist-submit"
          type="submit"
          disabled={loading}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-black uppercase tracking-[0.2em] font-bold px-10 py-4 hover:bg-white/85 transition-colors duration-300 disabled:opacity-60"
        >
          {loading && <Loader2 size={18} className="animate-spin" />}
          {loading ? "Joining..." : "Join the Waitlist"}
        </button>
      </div>
    </form>
  );
}
